import { z } from "zod";

export const reminderSequenceKindSchema = z.enum(["new-diary", "entry-reminders"]);
export const reminderChannelSchema = z.enum(["email", "sms"]);
export const reminderJourneyKeySchema = z.enum(["activation", "returning", "full", "short"]);

const timeSchema = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, "Use HH:mm in 24-hour time");

const unsafeCopy = /<\s*script|javascript:/i;

/** Clinician-authored reminder copy is plain text. Tags are removed before storage. */
export function plainReminderText(value: string): string {
    return value
        .replace(/<[^>]*>/g, "")
        .replace(/javascript:/gi, "")
        .trim();
}

const plainCopy = (max: number) =>
    z
        .string()
        .trim()
        .max(max)
        .transform((value) => plainReminderText(value));

export const REMINDER_FIXED_VARIABLE_IDS = [
    "first_name",
    "timing",
    "minutes",
    "open_forms_count",
    "due_dates_missed",
    "single_form_name",
    "completed_count",
    "time_taking_part",
    "is_final_milestone",
    "link",
] as const;

export const reminderFixedVariableIdSchema = z.enum(REMINDER_FIXED_VARIABLE_IDS);

const ruleClauseSchema = z.object({
    fact: reminderFixedVariableIdSchema,
    operator: z.enum(["is", "is-not", "at-least", "at-most"]),
    value: z.string().trim().max(60),
});

/** A list of checks joined by all or any. Decides whether a step sends and which wording option is used. */
export const reminderRuleSchema = z.object({
    match: z.enum(["all", "any"]),
    clauses: z.array(ruleClauseSchema).max(10),
});

export const reminderCustomVariableSchema = z.object({
    id: z.string().max(80).optional(),
    name: z
        .string()
        .regex(/^[a-z][a-z0-9_]*$/, "Use lower case letters, numbers and underscores")
        .max(40)
        .refine((name) => !(REMINDER_FIXED_VARIABLE_IDS as readonly string[]).includes(name), {
            message: "That name is used by a fixed variable",
        }),
    description: z.string().max(300).optional(),
    defaultText: plainCopy(2000),
    defaultSmsText: plainCopy(480),
    mode: z.enum(["rotate", "first-match"]),
    options: z
        .array(
            z.object({
                id: z.string().max(80).optional(),
                text: plainCopy(2000),
                smsText: plainCopy(480),
                when: reminderRuleSchema.nullable(),
            })
        )
        .max(30),
});

export const reminderStepCopySchema = z
    .object({
        emailSubject: plainCopy(200).optional(),
        emailBody: plainCopy(8000).optional(),
        smsBody: plainCopy(480).optional(),
    })
    .superRefine((copy, ctx) => {
        for (const [field, value] of Object.entries(copy)) {
            if (value && unsafeCopy.test(value)) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: "Copy cannot include script content",
                    path: [field],
                });
            }
        }
    });

export const reminderSequenceStepSchema = z
    .object({
        key: z.string().regex(/^[a-z0-9_-]+$/),
        offsetMinutes: z.number().int().min(0),
        channels: z.array(reminderChannelSchema).min(1),
        copyKeys: z.object({
            email: z.string().optional(),
            sms: z.string().optional(),
        }),
        copy: reminderStepCopySchema.optional(),
        /** Checked when the step is about to send. A step whose rule fails is skipped. Null or missing always sends. */
        sendWhen: reminderRuleSchema.nullable().optional(),
    })
    .superRefine((step, ctx) => {
        for (const channel of step.channels) {
            const hasInline =
                channel === "email"
                    ? Boolean(step.copy?.emailSubject || step.copy?.emailBody)
                    : Boolean(step.copy?.smsBody);
            if (!step.copyKeys[channel] && !hasInline) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: `Missing copy key for ${channel}`,
                    path: ["copyKeys", channel],
                });
            }
        }
    });

export const reminderJourneySchema = z
    .object({
        key: reminderJourneyKeySchema,
        steps: z.array(reminderSequenceStepSchema).min(1),
    })
    .superRefine((journey, ctx) => {
        const keys = new Set<string>();
        let previousOffset = -1;
        journey.steps.forEach((step, index) => {
            if (keys.has(step.key)) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: "Step keys must be unique",
                    path: ["steps", index, "key"],
                });
            }
            if (step.offsetMinutes < previousOffset) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: "Steps must be ordered by offset",
                    path: ["steps", index, "offsetMinutes"],
                });
            }
            keys.add(step.key);
            previousOffset = step.offsetMinutes;
        });
    });

export const reminderSequenceDefinitionSchema = z
    .object({
        schemaVersion: z.literal(1),
        /** Null preserves Knock's current recipient/account timezone fallback until cutover is approved. */
        timezone: z.string().min(1).nullable(),
        sendWindow: z.object({ start: timeSchema, end: timeSchema }),
        stop: z.object({
            maxMessages: z.number().int().positive(),
            maxInactiveDays: z.number().int().positive(),
            /** Give up once this many earlier due dates have passed without a completed check-in. Null or missing is off. */
            maxDueDatesMissed: z.number().int().positive().nullable().optional(),
        }),
        journeys: z.array(reminderJourneySchema).min(1),
        /** Wording the sequence owns. Referenced from copy as {name}. */
        variables: z.array(reminderCustomVariableSchema).max(30).optional(),
    })
    .superRefine((definition, ctx) => {
        const keys = new Set<string>();
        definition.journeys.forEach((journey, index) => {
            if (keys.has(journey.key)) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: "Journey keys must be unique",
                    path: ["journeys", index, "key"],
                });
            }
            keys.add(journey.key);
        });
        const variableNames = new Set<string>();
        (definition.variables ?? []).forEach((variable, index) => {
            if (variableNames.has(variable.name)) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: "Variable names must be unique",
                    path: ["variables", index, "name"],
                });
            }
            variableNames.add(variable.name);
        });
        if (definition.sendWindow.start >= definition.sendWindow.end) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: "Send window start must be before the end",
                path: ["sendWindow", "end"],
            });
        }
    });

export type ReminderSequenceKind = z.infer<typeof reminderSequenceKindSchema>;
export type ReminderChannel = z.infer<typeof reminderChannelSchema>;
export type ReminderJourneyKey = z.infer<typeof reminderJourneyKeySchema>;
export type ReminderStepCopy = z.infer<typeof reminderStepCopySchema>;
export type ReminderRule = z.infer<typeof reminderRuleSchema>;
export type ReminderCustomVariable = z.infer<typeof reminderCustomVariableSchema>;
export type ReminderFixedVariableId = z.infer<typeof reminderFixedVariableIdSchema>;
export type ReminderSequenceDefinition = z.infer<typeof reminderSequenceDefinitionSchema>;

export const DEFAULT_REMINDER_SEQUENCE_IDS: Record<ReminderSequenceKind, string> = {
    "new-diary": "00000000-0000-4000-8000-000000000101",
    "entry-reminders": "00000000-0000-4000-8000-000000000102",
};

const channelStep = (key: string, offsetMinutes: number) => ({
    key,
    offsetMinutes,
    channels: ["email", "sms"] as const,
    copyKeys: {
        email: `email_${key}`,
        sms: `sms_${key}`,
    },
});

export const DEFAULT_NEW_DIARY_REMINDER_DEFINITION: ReminderSequenceDefinition = reminderSequenceDefinitionSchema.parse(
    {
        schemaVersion: 1,
        timezone: null,
        sendWindow: { start: "07:00", end: "21:30" },
        stop: { maxMessages: 10, maxInactiveDays: 120 },
        journeys: [
            { key: "returning", steps: [channelStep("returning", 0)] },
            {
                key: "activation",
                steps: [
                    channelStep("day0", 0),
                    channelStep("day2", 2 * 1_440),
                    channelStep("day5", 5 * 1_440),
                    channelStep("day8", 8 * 1_440),
                    channelStep("day14", 14 * 1_440),
                ],
            },
        ],
    }
);

export const DEFAULT_ENTRY_REMINDER_DEFINITION: ReminderSequenceDefinition = reminderSequenceDefinitionSchema.parse({
    schemaVersion: 1,
    timezone: null,
    sendWindow: { start: "07:00", end: "21:30" },
    stop: { maxMessages: 16, maxInactiveDays: 120 },
    journeys: [
        {
            key: "full",
            steps: [0, 1, 2, 4, 8, 12, 16, 20].map((day, index) => channelStep(String(index + 1), day * 1_440)),
        },
        {
            key: "short",
            steps: [0, 3, 10].map((day, index) => channelStep(`short_${index + 1}`, day * 1_440)),
        },
    ],
});
